import React, { useState, useEffect, useRef, useCallback } from 'react';
import signupOnboardingService from '../../services/api/signupOnboardingService';

interface SignupOptimizingStepProps {
  productId: number;
  onComplete: (sessionId: string) => void;
  /** 409（生成済み）時: completeOnboarding → /plans へ親側で流す */
  onAlreadyGenerated: () => void;
  onSkip: () => void;
  onBack?: () => void;
}

interface OptimizingStepItem {
  label: string;
  activeLabel: string;
  icon: string;
}

const OPTIMIZING_STEPS: OptimizingStepItem[] = [
  { label: 'サービス情報の読み込みが完了しました', activeLabel: 'サービス情報を読み込み中...', icon: 'search' },
  { label: 'デザインスタイルの選定が完了しました', activeLabel: 'デザインスタイルを選定中...', icon: 'palette' },
  { label: 'バナー設計が完了しました', activeLabel: 'バナーを設計中...', icon: 'chart' },
  { label: '仕上げが完了しました', activeLabel: '仕上げ中...', icon: 'text' },
];

const STEP_INTERVAL_MS = 1750;
const MIN_DURATION_MS = 7000;

// --- Icons ---

const SearchIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChartIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const PaletteIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="10.5" r="2.5" />
    <circle cx="8.5" cy="7.5" r="2.5" />
    <circle cx="6.5" cy="12.5" r="2.5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

const TextIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 6H3" />
    <path d="M21 12H3" />
    <path d="M15.5 18H3" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const renderIcon = (iconType: string, color: string) => {
  switch (iconType) {
    case 'search': return <SearchIcon color={color} />;
    case 'chart': return <ChartIcon color={color} />;
    case 'palette': return <PaletteIcon color={color} />;
    case 'text': return <TextIcon color={color} />;
    default: return null;
  }
};

// --- Tailwind class constants (旧 styled-components) ---

const CONTAINER = 'w-full flex flex-col items-center py-8 px-0';

const TITLE =
  'text-[1.375rem] leading-[1.4] font-bold text-gray-800 mb-2 text-center';

const SUBTITLE = 'text-[0.875rem] text-gray-500 mb-8 text-center';

const SPINNER_CONTAINER = 'w-16 h-16 mb-8';

const SPINNER =
  'w-16 h-16 border-4 border-gray-200 border-t-4 border-t-violet-600 rounded-full animate-spin';

const STEPS_CONTAINER = 'w-full max-w-[400px] mb-6';

const STEP_ROW_BASE =
  'items-center py-3 px-0 animate-[sob-fade-in-x_0.4s_ease-out]';

const STEP_ICON_BASE =
  'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mr-[14px] transition-all duration-300 ease-[ease]';
const STEP_ICON_BY_STATUS: Record<
  'pending' | 'active' | 'completed',
  string
> = {
  completed: 'bg-violet-600',
  active:
    'bg-violet-100 animate-[sob-opacity-pulse_1.5s_ease-in-out_infinite]',
  pending: 'bg-gray-100',
};

const STEP_TEXT_BASE = 'text-[0.9rem]';
const STEP_TEXT_BY_STATUS: Record<
  'pending' | 'active' | 'completed',
  string
> = {
  completed: 'text-gray-700 font-normal',
  active: 'text-violet-600 font-medium',
  pending: 'text-gray-400 font-normal',
};

const PROGRESS_BAR_CONTAINER =
  'w-full max-w-[400px] h-1 bg-gray-200 rounded-sm overflow-hidden mt-2';

const PROGRESS_BAR_FILL_BASE =
  'h-full bg-gradient-to-r from-violet-600 to-violet-400 rounded-sm transition-[width] duration-500 ease-[ease]';
const PROGRESS_BAR_FILL_SWEEP =
  "after:content-[''] after:block after:w-full after:h-full " +
  'after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] ' +
  'after:animate-[sob-slide-in-x_1.5s_ease-in-out_infinite]';

const LINK_BUTTON =
  'bg-transparent bg-none border-none text-gray-400 text-[0.8125rem] cursor-pointer mt-6 py-2 px-4 ' +
  'transition-[color] duration-200 ease-[ease] hover:text-gray-500';

const ERROR_BOX = 'flex flex-col items-center gap-3 py-8 px-6 text-gray-500';

const ERROR_TITLE = 'text-[0.9375rem] font-semibold text-red-700 m-0';

const ERROR_TEXT =
  'text-[0.8125rem] text-gray-500 m-0 leading-[1.6] text-center';

const RETRY_BUTTON =
  'mt-1 py-2.5 px-6 text-[0.9375rem] font-semibold text-white bg-violet-600 border-none rounded-lg cursor-pointer ' +
  'transition-[background-color] duration-200 ease-[ease] hover:bg-violet-700';

const SignupOptimizingStep = React.memo<SignupOptimizingStepProps>(({
  productId,
  onComplete,
  onAlreadyGenerated,
  onSkip,
  onBack,
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [apiDone, setApiDone] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const startTimeRef = useRef(Date.now());
  const hasCalledCompleteRef = useRef(false);
  const mountedRef = useRef(true);
  const onCompleteRef = useRef(onComplete);
  const onAlreadyGeneratedRef = useRef(onAlreadyGenerated);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onAlreadyGeneratedRef.current = onAlreadyGenerated;
  }, [onAlreadyGenerated]);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // ステップ表示開始と同時に generate-banners を発火
  useEffect(() => {
    let cancelled = false;
    setApiError(false);
    setApiDone(false);
    setSessionId(null);
    hasCalledCompleteRef.current = false;
    startTimeRef.current = Date.now();

    signupOnboardingService.generateBanners(productId)
      .then((response) => {
        if (!cancelled && mountedRef.current) {
          setSessionId(response.session_id);
          setApiError(false);
          setApiDone(true);
        }
      })
      .catch((err: any) => {
        if (cancelled || !mountedRef.current) return;

        // 409: 生成済み → エラーにせずプラン選択へ
        if (err?.response?.status === 409) {
          hasCalledCompleteRef.current = true;
          onAlreadyGeneratedRef.current();
          return;
        }

        setApiError(true);
        setApiDone(true);
      });

    return () => { cancelled = true; };
  }, [productId, retryNonce]);

  const handleRetry = useCallback(() => {
    setApiError(false);
    setApiDone(false);
    setSessionId(null);
    setActiveStepIndex(0);
    setCompletedSteps(new Set());
    hasCalledCompleteRef.current = false;
    startTimeRef.current = Date.now();
    setRetryNonce((n) => n + 1);
  }, []);

  // ステップアニメーション
  useEffect(() => {
    if (apiError) return;

    const interval = setInterval(() => {
      setActiveStepIndex(prev => {
        if (prev < OPTIMIZING_STEPS.length - 1) {
          setCompletedSteps(s => new Set(s).add(prev));
          return prev + 1;
        }
        setCompletedSteps(s => new Set(s).add(prev));
        clearInterval(interval);
        return prev;
      });
    }, STEP_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [retryNonce, apiError]);

  const tryComplete = useCallback(() => {
    if (hasCalledCompleteRef.current) return;

    const allStepsCompleted = completedSteps.size === OPTIMIZING_STEPS.length;
    const elapsed = Date.now() - startTimeRef.current;
    const minDurationMet = elapsed >= MIN_DURATION_MS;

    if (allStepsCompleted && apiDone && minDurationMet && sessionId) {
      hasCalledCompleteRef.current = true;
      setTimeout(() => {
        if (mountedRef.current && sessionId) {
          onCompleteRef.current(sessionId);
        }
      }, 500);
    }
  }, [completedSteps, apiDone, sessionId]);

  useEffect(() => {
    tryComplete();
  }, [tryComplete]);

  // 最低表示時間待ち
  useEffect(() => {
    if (
      completedSteps.size === OPTIMIZING_STEPS.length &&
      apiDone &&
      sessionId &&
      !hasCalledCompleteRef.current
    ) {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = MIN_DURATION_MS - elapsed;
      if (remaining > 0) {
        const timer = setTimeout(tryComplete, remaining + 100);
        return () => clearTimeout(timer);
      }
    }
  }, [completedSteps, apiDone, sessionId, tryComplete]);

  const getStepStatus = (index: number): 'pending' | 'active' | 'completed' => {
    if (completedSteps.has(index)) return 'completed';
    if (index === activeStepIndex) return 'active';
    return 'pending';
  };

  const progressPercent = (completedSteps.size / OPTIMIZING_STEPS.length) * 100;

  if (apiError) {
    return (
      <div className={`${CONTAINER} signup-optimizing-step signup-optimizing-step--error`}>
        <h2 className={TITLE}>最適化に失敗しました</h2>
        <div className={ERROR_BOX}>
          <p className={ERROR_TITLE}>バナー生成を開始できませんでした</p>
          <p className={ERROR_TEXT}>
            通信状況やサーバーの状態により開始できないことがあります。
            <br />
            もう一度お試しいただくか、スキップして次へお進みください。
          </p>
          <button className={RETRY_BUTTON} type="button" onClick={handleRetry}>
            再試行する
          </button>
          <button className={LINK_BUTTON} type="button" onClick={onSkip}>
            スキップして次へ
          </button>
          {onBack && (
            <button className={LINK_BUTTON} type="button" onClick={onBack}>
              戻る
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${CONTAINER} signup-optimizing-step`}>
      <h2 className={TITLE}>今、Taskyがあなたのために<br />最適化しています</h2>
      <p className={SUBTITLE}>サービス情報をもとに専用バナーを準備しています</p>

      <div className={SPINNER_CONTAINER}>
        <div className={SPINNER} />
      </div>

      <div className={STEPS_CONTAINER}>
        {OPTIMIZING_STEPS.map((step, index) => {
          const status = getStepStatus(index);
          const isVisible = index <= activeStepIndex;
          const iconColor = status === 'completed' ? '#ffffff' :
            status === 'active' ? '#7c3aed' : '#9ca3af';

          return (
            <div
              key={index}
              className={`${isVisible ? 'flex' : 'hidden'} ${STEP_ROW_BASE}`}
            >
              <div className={`${STEP_ICON_BASE} ${STEP_ICON_BY_STATUS[status]}`}>
                {status === 'completed' ? <CheckIcon /> : renderIcon(step.icon, iconColor)}
              </div>
              <span className={`${STEP_TEXT_BASE} ${STEP_TEXT_BY_STATUS[status]}`}>
                {status === 'completed' ? step.label : step.activeLabel}
              </span>
            </div>
          );
        })}
      </div>

      <div className={PROGRESS_BAR_CONTAINER}>
        <div
          className={`${PROGRESS_BAR_FILL_BASE}${
            progressPercent < 100 ? ` ${PROGRESS_BAR_FILL_SWEEP}` : ''
          }`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <button className={LINK_BUTTON} type="button" onClick={onSkip}>
        スキップ
      </button>
    </div>
  );
});

SignupOptimizingStep.displayName = 'SignupOptimizingStep';

export default SignupOptimizingStep;
